import { Switch } from "antd";
import {
  MaterialBalanceTimeChartProducts,
  ProductKind,
  ProductTypeT,
  RecoveryTimeChartProducts,
  SalesProduct,
  ThroughputProduct
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { getEffectiveProductLineStyle } from "entities/charts/eva/translators/getEffectiveProductLineStyle";
import styled from "styled-components";
import { getProductTooltip } from "utils";
import { getProductIcon } from "utils/chart/getProductIcon";
import { getProductKey } from "utils/chart/getProductKey";
import { getShortenedProductName } from "utils/chart/productNameHelpers";

import useFeatureFlags from "hooks/useFeatureFlags";

import { Tooltip } from "components/base";
import { getProductLabel } from "components/charts/utils/getProductLabel";

import ProductHeader from "../../ProductHeader";
import { ProductButton } from "../ProductSelector.styles";
import { LineStylePicker } from "./LineStylePicker";
import { LineStylePreview } from "./LineStylePreview";
import { PrimarySecondaryIndicator } from "./PrimarySecondaryIndicator";

export const LiquidYieldsProductHeader = ({
  selectedProductGroup,
  chartType,
  yieldsType,
  rawLabel,
  salesLabel,
  onChangeType,
  onProductClick,
  product,
  inletRateLabel,
  outletRateLabel,
  entityKind,
  isMultiProductActive,
  primaryProducts = [],
  secondaryProducts = [],
  handleSetPrimary,
  handleSetSecondary,
  canAddProduct,
  productLineStyles = {},
  multiProduct,
  updateLineStyle
}) => {
  const { hasFeature } = useFeatureFlags();

  if (
    selectedProductGroup !== ProductKind.Sales &&
    selectedProductGroup !== ProductKind.Throughput
  ) {
    return null;
  }
  const index = yieldsType === rawLabel ? 2 : 3;
  const header: ProductTypeT =
    selectedProductGroup === ProductKind["Throughput"]
      ? ThroughputProduct[index]
      : SalesProduct[index];

  function removeCO2Strings(items) {
    return items.filter((item) => !item.includes("CO₂"));
  }
  if (!hasFeature("Midstream Allocation Model")) {
    header.items = removeCO2Strings(header.items);
  }
  return (
    <>
      <ButtonContainer>
        <Switch
          unCheckedChildren={rawLabel}
          checkedChildren={salesLabel}
          checked={yieldsType === salesLabel}
          onChange={onChangeType}
          size="small"></Switch>
      </ButtonContainer>

      <ProductHeader key={header.name} header={header.name}>
        {header.items.map((prod) => {
          const productToAdd = getProductKey(prod);
          const isPrimaryContainingProduct = primaryProducts.includes(productToAdd);
          const isSecondaryContainingProduct = secondaryProducts.includes(productToAdd);

          const isPrimaryDisabled =
            !isPrimaryContainingProduct && !canAddProduct?.(prod, "primary");
          const isSecondaryDisabled =
            !isSecondaryContainingProduct && !canAddProduct?.(prod, "secondary");
          const isBothPSDisabled =
            isMultiProductActive && isPrimaryDisabled && isSecondaryDisabled;

          // Charts that restrict to a fixed product set (RF vs Time → Oil/Gas,
          // Material Balance → Oil/Gas/Water/Total/BOE) never allow yield products.
          const isChartRestrictedProduct =
            (chartType === EvaChart.RecoveryTime &&
              !RecoveryTimeChartProducts.includes(prod)) ||
            (chartType === EvaChart.MaterialBalanceTime &&
              !MaterialBalanceTimeChartProducts.includes(prod));

          const isProductDisabled = isChartRestrictedProduct || isBothPSDisabled;

          return (
            <Tooltip key={prod} title={getProductTooltip(prod, selectedProductGroup)}>
              <ProductButton
                key={prod}
                onClick={() => {
                  if (isProductDisabled) return;
                  onProductClick(prod, isPrimaryDisabled, isSecondaryDisabled);
                }}
                className={`${isMultiProductActive ? "isMultiProduct" : ""} ${
                  isMultiProductActive
                    ? isPrimaryContainingProduct || isSecondaryContainingProduct
                      ? "isSelected"
                      : null
                    : prod === product
                    ? "isSelected"
                    : null
                } ${isProductDisabled ? "isDisabled" : ""}`}>
                {isMultiProductActive && (
                  <PrimarySecondaryIndicator
                    isPrimary={isPrimaryContainingProduct}
                    isSecondary={isSecondaryContainingProduct}
                    onSetPrimary={(e) => handleSetPrimary(prod, e)}
                    onSetSecondary={(e) => handleSetSecondary(prod, e)}
                    isPrimaryDisabled={isPrimaryDisabled}
                    isSecondaryDisabled={isSecondaryDisabled}
                  />
                )}
                {getProductIcon(prod)}
                <span>
                  {getShortenedProductName(
                    getProductLabel(prod, entityKind),
                    rawLabel,
                    salesLabel,
                    inletRateLabel,
                    outletRateLabel
                  )}
                </span>
                {isMultiProductActive &&
                  (isPrimaryContainingProduct || isSecondaryContainingProduct) &&
                  productLineStyles[productToAdd] && (
                    <LineStylePicker
                      currentStyle={getEffectiveProductLineStyle(
                        productToAdd,
                        multiProduct,
                        productLineStyles[productToAdd]
                      )}
                      onSelect={(style) => updateLineStyle(prod, style)}>
                      <LineStylePreview
                        style={getEffectiveProductLineStyle(
                          productToAdd,
                          multiProduct,
                          productLineStyles[productToAdd]
                        )}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </LineStylePicker>
                  )}
              </ProductButton>
            </Tooltip>
          );
        })}
      </ProductHeader>
    </>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  transform: translate(-10px, 30px);
  margin-top: -16px;
  position: relative;
  z-index: 1;

  .ant-switch {
    background-color: var(--color-primary);
  }
`;
